/**
 * A route-level lazy component that can also be loaded ahead of rendering (ADR-0061). In the browser it behaves like
 * React.lazy (its own chunk, a Suspense fallback while it loads). `preload()` fetches the chunk before a render: the
 * build-time prerender loads every public page first so the HTML is complete, and the browser loads the open page's
 * chunks before hydrating it, so hydration meets no Suspense and the prerendered HTML stays as it is.
 */
import { type ComponentType, createElement, lazy } from "react";

export type Preloadable<P> = ComponentType<P> & { preload: () => Promise<unknown> };

export function preloadable<P extends object>(load: () => Promise<ComponentType<P>>): Preloadable<P> {
  let loaded: ComponentType<P> | undefined;
  let pending: Promise<ComponentType<P>> | undefined;
  const fetch = () =>
    (pending ??= load().then((c) => {
      loaded = c;
      return c;
    }));
  const Lazy = lazy(() => fetch().then((c) => ({ default: c })));
  const Page = (props: P) => (loaded ? createElement(loaded, props) : createElement(Lazy as ComponentType<P>, props));
  return Object.assign(Page, { preload: fetch });
}
